import { useState } from "react";
import { Heart, MessageCircle, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import api, { formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import VerifiedBadge from "@/components/VerifiedBadge";
import ScholarAvatar from "@/components/ScholarAvatar";
import ShareMenu from "@/components/ShareMenu";

function timeAgo(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
}

export default function PostCard({ post, onChanged }) {
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentCount, setCommentCount] = useState(post.comment_count);
  const author = post.author || {};

  const toggleLike = async () => {
    try {
      if (liked) {
        await api.delete(`/posts/${post.id}/like`);
        setLiked(false); setLikeCount((c) => Math.max(0, c - 1));
      } else {
        await api.post(`/posts/${post.id}/like`);
        setLiked(true); setLikeCount((c) => c + 1);
      }
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadComments = async () => {
    if (!showComments) {
      try {
        const { data } = await api.get(`/posts/${post.id}/comments`);
        setComments(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setShowComments((s) => !s);
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { content: commentText });
      setComments((c) => [...c, data]);
      setCommentText("");
      setCommentCount((n) => n + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <article data-testid="post-card" className="paper-card p-6 animate-fade-up">
      <div className="flex items-start gap-3">
        <Link to={`/app/scholars/${author.id}`}>
          <ScholarAvatar user={author} size={44} />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <Link to={`/app/scholars/${author.id}`} className="font-semibold text-[color:var(--ayur-ink)] link-underline inline-flex items-center gap-1">
              {author.full_name || "Unknown scholar"}
              {author.verified && <VerifiedBadge size={16} />}
            </Link>
            {author.degree && <span className="pill">{author.degree}</span>}
            {author.specialty && <span className="text-xs text-[color:var(--ayur-mute)]">· {author.specialty}</span>}
          </div>
          <div className="text-xs text-[color:var(--ayur-mute)] mt-0.5">
            {author.college}{author.batch_year ? ` · Batch ${author.batch_year}` : ""}
          </div>
        </div>
        <div className="text-xs text-[color:var(--ayur-mute)] whitespace-nowrap">{timeAgo(post.created_at)}</div>
      </div>

      <div className="mt-4 whitespace-pre-wrap text-[color:var(--ayur-ink)] leading-relaxed">
        {post.content}
      </div>

      {post.image_url && (
        <img src={resolveImageUrl(post.image_url)} alt="" className="mt-4 rounded-lg border border-[color:var(--ayur-border)] max-h-[420px] w-full object-cover" />
      )}

      {post.specialty && (
        <div className="mt-4">
          <span className="pill">{post.specialty}</span>
        </div>
      )}

      <div className="mt-5 flex items-center gap-2 border-t border-[color:var(--ayur-border)] pt-3">
        <Button
          data-testid="post-like-button"
          variant="ghost" size="sm" onClick={toggleLike}
          className={liked ? "text-[color:var(--ayur-terracotta)]" : "text-[color:var(--ayur-ink-2)]"}
        >
          <Heart className={`w-4 h-4 mr-1 ${liked ? "fill-current" : ""}`} /> {likeCount}
        </Button>
        <Button
          data-testid="post-comment-button"
          variant="ghost" size="sm" onClick={loadComments}
          className="text-[color:var(--ayur-ink-2)]"
        >
          <MessageCircle className="w-4 h-4 mr-1" /> {commentCount}
        </Button>
        <div className="ml-auto">
          <ShareMenu
            url={`${window.location.origin}/post/${post.id}`}
            title={`${author.full_name || "A scholar"} on VaidGram`}
            body={post.content?.slice(0, 180)}
            testid="post-share-menu"
          />
        </div>
      </div>

      {showComments && (
        <div className="mt-4 space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              <ScholarAvatar user={c.author} size={32} />
              <div className="flex-1 bg-[color:var(--ayur-cream-2)] rounded-lg px-3 py-2">
                <div className="text-sm font-semibold inline-flex items-center gap-1">
                  {c.author?.full_name}
                  {c.author?.verified && <VerifiedBadge size={13} />}
                </div>
                <div className="text-sm text-[color:var(--ayur-ink-2)] whitespace-pre-wrap">{c.content}</div>
              </div>
            </div>
          ))}
          <form onSubmit={addComment} className="flex gap-2">
            <Input
              data-testid="post-comment-input"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a scholarly note…"
              className="bg-white"
            />
            <Button type="submit" data-testid="post-comment-submit-button" className="bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </article>
  );
}
